import { useEffect, useState } from 'react'

import { useLocation, useNavigate } from 'react-router'

/**
 * 画面を開いた時の location state (別の画面が渡した完了の案内など) を 1 回だけ読み、 読んだら履歴の
 * state から消す (再読み込み・戻る / 進むで同じ案内をもう一度出さない。 パス・クエリ・ハッシュはそのまま)。
 * 読んだ値は state に移すので、 dismiss を呼ぶまで案内は残る。
 *
 * 初回レンダーで値が分かるので、 呼び出し側は画面の初期状態 (タブ・受渡日) をこの値から決められる。
 * `read` は形が違う state に対して undefined を返す (location state は履歴に残るので型を信用しない)。
 * effect の依存に入るので、 参照の安定した関数 (module 定数か useCallback) を渡す。
 */
export function useLocationStateNotice<T>(
  read: (state: unknown) => T | undefined,
): [T | undefined, () => void] {
  const location = useLocation()
  const navigate = useNavigate()
  const [value, setValue] = useState(() => read(location.state))

  useEffect(() => {
    if (read(location.state) === undefined) return
    void navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null },
    )
  }, [read, location, navigate])

  return [value, () => setValue(undefined)]
}
